import { Injectable, ElementRef } from '@angular/core';
import { FormControl, UntypedFormGroup, AbstractControl } from '@angular/forms';
import * as moment from 'moment';
import { DialogService } from '../dialog/dialog.service';
import { HeaderType } from 'src/app/dataModels/header-type';
import { AuthService } from '../authManager/auth-manager.service';
import { CONSTANTS } from 'src/app/dataModels/constants';
import { saveAs } from 'file-saver';
import * as base64 from 'base64-js';
import { TextDecoderLite, TextEncoderLite } from 'text-encoder-lite';
import { TelephonePipe } from 'src/app/pipes/telephone.pipe';

/**
 * Utility service for the application
 */
@Injectable({
  providedIn: 'root',
})
export class HelperService {
  /**
   * Constructor to inject dependency
   * @param dialogService instance of dialogService
   * @param authService instnace of AuthService
   */
  constructor(private dialogService: DialogService, private authService: AuthService, private telephonePipe: TelephonePipe) {
    this.authService.isLoggedIn.subscribe((value) => {
      this.isUserLoggedIn = value;
    });
  }

  /**
   * indicate if user is logged in
   */
  isUserLoggedIn;

  /**
   * Check if object is null or undefined
   * @param obj Object to check
   */
  IsNullOrEmpty(obj) {
    if (obj === null || typeof obj === 'undefined') {
      return true;
    } else if (obj.length === 0 || obj === 'null') {
      return true;
    } else {
      return false;
    }
  }

  /**
   * encode json, replace new line, double quote
   * @param source value to be encoded
   * @param encodeFlag flag to encode with URI
   */
  encodeJsonString(source, encodeFlag?) {
    if (!this.IsNullOrEmpty(source)) {
      source = source
        .replace(/[\b]/g, '<<b>>')
        .replace(/[\f]/g, '<<f>>')
        .replace(/[\n]/g, '<<n>>')
        .replace(/[\r]/g, '<<r>>')
        .replace(/[\t]/g, '<<t>>')
        .replace(/"/g, '<<quot>>');
      if (encodeFlag) {
        source = encodeURIComponent(source);
      }
    } else {
      source = '';
    }
    return source;
  }

  /**
   * decode the encoded value
   * @param source value to be decoded
   */
  decodeJsonString(source) {
    if (!this.IsNullOrEmpty(source)) {
      try {
        source = decodeURI(decodeURIComponent(source));
      } catch (e) {
        try {
          source = decodeURIComponent(source);
        } catch (e) {}
      }
      try {
        source = source
          .replace('<<n>>', '\n')
          .replace(/<<b>>/g, '\b')
          .replace(/<<f>>/g, '\f')
          .replace(/<<n>>/g, '\n')
          .replace(/<<r>>/g, '\r')
          .replace(/<<t>>/g, '\t')
          .replace(/\\/g, '')
          .replace(/<<quot>>/g, '"');
      } catch (e) {
        source = '';
      }
    } else {
      source = '';
    }
    return source;
  }

   /**
   * decode the encoded value
   * @param source value to be decoded
   */
  decodeJsonStringTrim(source) {
    if (!this.IsNullOrEmpty(source)) {
      try {
        source = decodeURI(decodeURIComponent(source));
      } catch (e) {
        try {
          source = decodeURIComponent(source);
        } catch (e) {}
      }
      try {
        source = source
          .replace('<<n>>', '')
          .replace(/<<b>>/g, '')
          .replace(/<<f>>/g, '')
          .replace(/<<n>>/g, '')
          .replace(/<<r>>/g, '')
          .replace(/<<t>>/g, '')
          .replace(/\\/g, '')
          .replace(/<<quot>>/g, '"');
          source = source.trim()
      } catch (e) {
        source = '';
      }
    } else {
      source = '';
    }
    return source;
  }


  /**
   * Date when the application prod build was performed
   */
  getReleaseDateForFooter() {
    return '10/16/2024';
  }

  /**
   * determine if a control is invalid
   * @param isSubmit is form submitted
   * @param ctrl control to check invalid for
   */
  isControlInvalid(isSubmit: boolean, ctrl: AbstractControl) {
    return (isSubmit || (ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched))) && ctrl && ctrl.errors;
  }

  // isZipControlInvalid(isSubmit: boolean, ctrl: AbstractControl): boolean {
  //   if (ctrl.errors && (ctrl.dirty || ctrl.touched || isSubmit)) {
  //     const value = ctrl.value;
  //     // Hide the error if the value is exactly 5 digits and the rest of the control is valid
  //     if (value.length === 5 && !ctrl.errors?.pattern) {
  //       return false;
  //     }
  //     return true;
  //   }
  //   return false;
  // }

  /**
   * format date to 'mm/dd/yyyyy' string using momentJS
   * @param date date to be formatted
   * @param appendTime boolean to indicate if time needs to be appended
   */
  formatDate(date, appendTime = false) {
    if (date && appendTime) {
      return moment(date).format('MM/DD/YYYY HH:mm:ss');
    } else if (date) {
      return moment(date).format('MM/DD/YYYY');
    } else {
      return '';
    }
  }

  /**
   * show message
   * @param title title of the message
   * @param message message
   * @param type type of message
   */
  showMessage(title, message, type) {
    return this.dialogService.showMessage({
      title: title || 'Info',
      message,
      btnOptions: ['Ok'],
      type,
    });
  }

  /**
   * Display Information message
   * @param message message to be displayed
   * @param title title for the message
   */
  showInfoMessage(message, title?) {
    return this.dialogService.showMessage({
      title: title || 'Info',
      message,
      btnOptions: ['Ok'],
      type: HeaderType.INFO,
    });
  }

  /**
   * Display Error message
   * @param message message to be displayed
   * @param title title for the message
   */
  showErrorMessage(message, title?) {
    return this.dialogService.showMessage({
      title: title || 'Error',
      message: message || CONSTANTS.ERROR_MESSAGE,
      btnOptions: ['Ok'],
      type: HeaderType.ERROR,
    });
  }

  /**
   * Display Warning message
   * @param message message to be displayed
   * @param title title for the message
   */
  showWarningMessage(message, title?) {
    return this.dialogService.showMessage({
      title: title || 'Warning',
      message,
      btnOptions: ['Ok'],
      type: HeaderType.WARNING,
    });
  }

  /**
   * Display Success message
   * @param message message to be displayed
   * @param title title for the message
   */
  showSuccessMessage(message, title?) {
    return this.dialogService.showMessage({
      title: title || 'Success',
      message,
      btnOptions: ['Ok'],
      type: HeaderType.SUCCESS,
    });
  }

  /**
   * Display Confirmation message
   * @param message message to be displayed
   * @param title title for the message
   */
  showConfirmMessage(message, btnOptions: string[], title?) {
    return this.dialogService.showMessage({
      title: title || 'Confirm',
      message,
      btnOptions: btnOptions ? btnOptions : ['Ok', 'Cancel'],
      type: HeaderType.CONFIRM,
    });
  }

  /**
   * Capitalize the string
   * @param value value to be capitalized
   */
  capitalize(value: string) {
    return value
      .toLowerCase()
      .split(' ')
      .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
      .join(' ');
  }

  /**
   * Capitalize the string
   * @param value value to be capitalized
   */
  formatSideBarNavForNavigation(value: string) {
    value = value.replace(/[-_\s.]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ''));
    return value.substr(0, 1).toLowerCase() + value.substr(1);
  }

  /**
   * get the address in single string
   * @param address address obj
   */
  formatAddress(address) {
    let formattedAddress = '';
    if (!this.IsNullOrEmpty(address.addressLine1)) {
      formattedAddress += address.addressLine1;
    }
    if (!this.IsNullOrEmpty(address.addressLine2)) {
      formattedAddress += `, ${address.addressLine2}`;
    }
    if (!this.IsNullOrEmpty(address.city)) {
      formattedAddress += `, ${address.city}`;
    }
    if (!this.IsNullOrEmpty(address.state)) {
      formattedAddress += `, ${address.state}`;
    }
    if (!this.IsNullOrEmpty(address.zip)) {
      formattedAddress += `, ${address.zip}`;
    }

    return formattedAddress;
  }

  /**
   * Returns if the component is deactivated.
   * Helps to confirm user to deactivate a component.
   * @param form Form which is deactivated
   */
  canDeactivate(form: UntypedFormGroup) {
    return form?.dirty && this.isUserLoggedIn && form?.touched;
  }
  

  /**
   * Resets form and sets it to Pristine and Touched
   * @param form Form to cleared
   */
  resetForm(form: UntypedFormGroup) {
    form.reset();
    form.markAsPristine();
    form.markAsUntouched();
  }

  /**
   * Returns list of objects to be bound as data for multiselect dropdown
   * @param list List bound to multiselect dropdown
   * @param valueList List of values selected
   */
  formMultiSelectListToBind(list, valueList) {
    let isFound = false;
    const boundList = [];
    for (const value of list) {
      for (const selectedValue of valueList) {
        if (value.columnValue === selectedValue) {
          isFound = true;
          break;
        }
      }

      if (isFound) {
        boundList.push(value);
      }

      isFound = false;
    }

    return boundList;
  }

  /**
   * check if the response is valid for get bundle calls
   * @param result result from the api
   */
  isValidGetResult(result) {
    return (
      (result.status === CONSTANTS.SUCCESS || result.status === CONSTANTS.TRN_WARNING) &&
      result.objects &&
      result.objects.length > 0 &&
      result.objects[0]
    );
  }

  /**
   * download the file using FileSaver saveAs
   * @param fileObj file object
   */
  downloadEncodedFile(fileObj) {
    const decodedStream = base64.toByteArray(fileObj.encodedData);
    const stream = new TextDecoderLite().decode(decodedStream);
    const blob = this.b64toBlob(stream.split(',')[1], fileObj.fileType);
    saveAs(blob, fileObj.fileName);
  }

  /**
   * Convert base64 to blob
   * @param b64Data base64 data
   * @param contentType file contentType
   * @param sliceSize file sliceSize
   */
  b64toBlob(b64Data, contentType, sliceSize?) {
    contentType = contentType || '';
    sliceSize = sliceSize || 512;

    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, {
      type: contentType,
    });
    return blob;
  }

  /**
   * encode the file data using TextEncoderLite
   * @param fileData file
   */
  encodeFileStream(fileData) {
    return new TextEncoderLite().encode(fileData);
  }

  /**
   * convert the encoded file data to base64
   * @param encodedFileData encoded file data
   */
  encodeToBase64(encodedFileData) {
    return base64.fromByteArray(encodedFileData);
  }

  /**
   * calculate age
   * @param dateString date in 'mm/dd/yyyy' format
   */
  calculateAge(dateString) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const yearNow = now.getFullYear();
    const monthNow = now.getMonth();
    const dateNow = now.getDate();

    const dob = new Date(dateString.substring(6, 10), dateString.substring(0, 2) - 1, dateString.substring(3, 5));

    const yearDob = dob.getFullYear();
    const monthDob = dob.getMonth();
    const dateDob = dob.getDate();
    let age = {
      years: 0,
      months: 0,
      days: 0,
    };

    let yearAge = yearNow - yearDob;
    let monthAge;
    let dateAge;
    if (monthNow >= monthDob) {
      monthAge = monthNow - monthDob;
    } else {
      yearAge--;
      monthAge = 12 + monthNow - monthDob;
    }

    if (dateNow >= dateDob) {
      dateAge = dateNow - dateDob;
    } else {
      monthAge--;
      dateAge = 31 + dateNow - dateDob;

      if (monthAge < 0) {
        monthAge = 11;
        yearAge--;
      }
    }

    age = {
      years: yearAge,
      months: monthAge,
      days: dateAge,
    };

    return age;
  }

  /**
   * format the age in years, months
   * @param age age of person
   */
  formatAge(age) {
    let yearString;
    let monthString;
    let dayString;

    if (age.years > 1) {
      yearString = ' yr(s)';
    } else {
      yearString = ' year';
    }
    if (age.months > 1) {
      monthString = ' month(s)';
    } else {
      monthString = ' month';
    }
    if (age.days > 1) {
      dayString = ' days';
    } else {
      dayString = ' day';
    }
    return age.years + ' yr(s) ' + age.months + ' month(s) ';
  }

  /**
   * Check if an object is empty
   * @param obj object to check if its empty
   */
  isEmptyObject(obj) {
    if (obj && Object.keys(obj).length === 0) {
      return true;
    } else if (obj) {
      let isObjectEmpty = true;
      Object.keys(obj).forEach((o) => {
        if (!this.IsNullOrEmpty(obj[o])) {
          isObjectEmpty = false;
        }
      });

      return isObjectEmpty;
    } else {
      return true;
    }
  }

  /**
   * if county code is 4 digits, append zeros to the end and return the formatted county code
   */
  formatCountyCode(countyCode) {
    if (countyCode?.length === 2) {
      countyCode = `${countyCode}00`;
    }

    return countyCode;
  }

  /**
   * append user and date along with comments
   * @param comments comments
   * @param appendTime does time need to be appended
   * @param userName userName to be appended
   */
  appendUserAndDateForComments(comments: string, userName, appendTime?: boolean) {
    if (comments !== null && typeof comments !== 'undefined' && comments !== '') {
      return `${userName} ${this.formatDate(new Date(), appendTime)} ${this.encodeJsonString(comments).trim()}`;
    } else {
      return '';
    }
  }

  /**
   * utility method to check if the field is of type date
   * @param value date value
   */
  isValidDate(value) {
    let date = moment(value).format('mm/dd/yyyy');
    return date.toUpperCase() !== 'INVALID DATE';
  }

  /**
   * scroll to element
   * @param element element to be bought into view
   */
  scrollToElement(element: ElementRef) {
    setTimeout(() => {
      element.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 500);
  }

  /**
   * return the formatted date
   * @param date date
   * @param format format for the date
   */
  convertDate(date, format) {
    return moment(date).format(format);
  }

  /**
   * determine if header is part of export
   * @param header header
   */
  isHeaderPartOfExport(header) {
    return !this.IsNullOrEmpty(header.text) &&
    header.text !== 'Info' &&
    header.text !== "Edit" &&
    header.text !== "Delete" &&
    header.text !== "Update" &&
    header.text !== "Record Temperatures" &&
    header.text !== "View Logs" &&
    header.text !== "View"
  }

  /**
   * transform value to telephone format
   * @param value value to be transformed in telephone format
   */
  formatPhone(value) {
    return this.telephonePipe.transform(value);
  }

  /**
   * check if object has all empty properties
   * @param obj object to check all empty properties
   */
  objectHasEmptyData(obj) {
    let objHasEmptyData = true;
    Object.keys(obj).forEach((key) => {
      if (obj[key] instanceof Array && obj[key].length > 0 && !this.IsNullOrEmpty(obj[key][0])) {
        objHasEmptyData = false;
      } else if (!this.IsNullOrEmpty(obj[key])) {
        objHasEmptyData = false;
      }
    });

    return objHasEmptyData;
  }

  /**
   * get the value of the checkbox
   * @param checkboxValue checkbox value to be checked for null or empty
   */
  getCheckboxValue(checkboxValue) {
    return checkboxValue && checkboxValue[0] ? checkboxValue[0] : '';
  }

  /**
   * check if password meets requirement
   * @param password password entered
   */
  checkPasswordPattern(password) {
    const lowecaseRegex = /^(?=.*[a-z]).+$/;
    const uppercaseRegex = /^(?=.*[A-Z]).+$/;
    const numberRegex = /^(?=.*[\d]).+$/;
    const splCharRegex = /^(?=.*[~!@#$%^&*_+=|\\(\){}[\]:;"<>,.?/ \-]).+$/;
    return {
      lengthPattern: password.length >= 8,
      maxLengthPattern: password.length >= 8 && password.length <= 15,
      upperCasePattern: uppercaseRegex.test(password),
      lowerCasePattern: lowecaseRegex.test(password),
      numberPattern: numberRegex.test(password),
      splCharPattern: splCharRegex.test(password),
    };
  }

  /**
   * download the file using FileSaver saveAs
   * @param fileObj file object
   */
  downloadCSVFile(fileObj) {
    const decodedStream = base64.toByteArray(fileObj.encodedData);
    const blob = new Blob([decodedStream], { type: fileObj.fileType });
    saveAs(blob, fileObj.fileName);
  }

  /**
   * append leading zeros
   * @param num nubmer to be prepended with zeros
   * @param size size of the field
   */
  padLeadingZeros(num, size) {
    let s = num + '';
    while (s.length < size) {
      s = '0' + s;
    }
    return s;
  }

  /**
   * check if the response returned from the api call is success
   * @param result result received from api call
   */
  isValidPostResponse(result) {
    return result.status === CONSTANTS.SUCCESS || result.status === CONSTANTS.TRN_WARNING || result.status === CONSTANTS.TRN_IN_PROGRESS;
  }

  /**
   * download file
   * @param fileData file to be downloaded
   * @param fileName name of file
   */
  downloadBlob(fileData, fileName) {
    const blob = new Blob([fileData]);
    saveAs(blob, fileName);
  }

  /**
   * decode base64 to string
   * @param base64Str base64 string
   */
  decodeBase64ToString(base64Str) {
    if (base64Str) {
      const byteArr = base64.toByteArray(base64Str);
      return new TextDecoderLite().decode(byteArr);
    } else {
      return '';
    }
  }

  /**
   * handle download response
   * @param response respsonse
   */
  handleDownloadResponse(response) {
    if (response.type === 'application/json') {
      const fileReader = new FileReader();
      fileReader.readAsText(response);
      fileReader.onloadend = (e) => {
        response = JSON.parse(e.target.result as string);
        this.showErrorMessage(response.message);
      };
    }
  }

  /**
   * get number of days between dates
   * @param startDate start date
   * @param endDate end date
   */

  getNumberOfDays(startDate: Date, endDate: Date): any {
    const start = moment(startDate);
    const end = moment(endDate);
    return end.diff(start, 'days');
  }

  /**
   * get vfc status list
   */
  getVFCStatusList() {
    return [
      {
        columnValue: 'ACTIVE',
        descr: 'Active',
        displayOrder: 0,
      },
      {
        columnValue: 'INACTIVE',
        descr: 'In Active',
        displayOrder: 1,
      },
      {
        columnValue: 'DEPARTED',
        descr: 'Departed',
        displayOrder: 2,
      },
      {
        columnValue: 'ALL',
        descr: 'ALL',
        displayOrder: 3,
      },
    ];
  }

  /**
   * navigate to hippocrates
   */
  navigateToHippocrates() {
    window.open('https://njiis-support.nj.gov/doh?id=doh_sc_category&sys_id=b9b2cd131beb595070900f66624bcb2f&catalog_id=b4124d131beb595070900f66624bcbab', '_blank')
    // window.open('https://njdeptofhealth.atlassian.net/servicedesk/customer/portal/4', '_blank');
  }

  /**
   * deep copy an array with objects
   * @param sourceArr array which has to be copied
   */
  copyArrayWithObjects(sourceArr) {
    return JSON.parse(JSON.stringify(sourceArr));
  }

  /**
   * check if value is null and return empty string if so
   */
  isValue(str) {
    return str ? str : '';
  }

  /**
   * download file
   * @param csvData csvData to be downloaded
   * @param fileName fileName for the downloaded file
   */
  downloadCsvData(csvData, fileName) {
    const blob = new Blob([csvData]);
    saveAs(blob, `${fileName}_${this.convertDate(new Date(), 'DD_MMM_YYYY_HH_mm_ss')}.csv`);
  }

  /**
   * download file
   * @param txtFileData txtFile to be
   * @params action type of CVRS file being downloaded ADD, UPDATE or DELETE
   * @param index numbering for the file
   */
  downloadCVRSData(txtFileData, action, index) {
    const blob = new Blob([txtFileData]);
    const fileCounter = index < 10 ? '0' + (index + 1) : index + 1;
    saveAs(blob, `${this.convertDate(new Date(), 'YYYYMMDD')}_${fileCounter}_NJA_${action.substring(0, 1)}.txt`);
  }

  /**
   * get active county and municipality
   * @param countyMuniHrchyList list of countyMunicipality
   * @returns list of active countyMunicipality
   */
  getActiveCountyAndMunicipalityList(countyMuniHrchyList: any[]): any[] {
    let countyMuniList = [];

    for (let k = 0; k < countyMuniHrchyList.length; k++) {
      countyMuniList.push({
        countyName: countyMuniHrchyList[k].countyName,
        countyCode: countyMuniHrchyList[k].countyCode,
        municipality: countyMuniHrchyList[k].municipality.filter((x) => x.status === 'ACTIVE'),
      });
    }

    return countyMuniList;
  }

  /**
   * scroll a page to top
   */
  scrollToTop() {
    window.scrollTo(0, 0);
  }

  
}
